/**
 * synergies.ts — build combo / synergy 偵測 (Phaser-free 純資料)。
 *
 * 設計動機 (docs/spec/GAME_DESIGN_RESEARCH.md):升級卡原本各自孤立加值,缺
 * 「我湊出 combo」的爽感。這裡定義 2-3 組 K-3 看得懂、因果明確的「卡片組合 →
 * 額外效果」,讓 build 有組合爽感。
 *
 * 鐵則(學習與遊戲分離,最高優先):
 *   - synergy 純當局戰力:只讀/寫 RunModifiers (跟升級卡同性質,跨場歸零)。
 *   - 絕不碰 progress / factionXP / freeXP / skillLevels / 傷害永久值。
 *   - 戰鬥中不彈題。
 *   - K-3 易懂:只 3 組、因果直接 (有 A 又有 B → 看得到 combo)。
 *
 * 用法:scene 在「每次升級套用後」呼 detectSynergies(mods),它回傳這局「現在
 * 成立」的 synergy 清單,並把對應的 synergy 旗標寫進 mods (idempotent — 重複呼
 * 叫不會疊加,因為效果是「設定旗標/設定上限值」而非「累加」)。scene 再:
 *   1. 對「新成立」的 synergy 跳純 FX 提示 (combo!)。
 *   2. 每幀/每碰撞讀 mods 的 synergy 旗標施工額外效果。
 */

import type { RunModifiers } from './run-modifiers';

/** synergy 識別碼。K-3 友善的固定 3 組。 */
export type SynergyId = 'chain-blast' | 'lucky-spread' | 'gale-rush';

/** 一組 synergy 的靜態定義 (給 UI / FX 顯示用,純資料)。 */
export interface SynergyDef {
  id: SynergyId;
  /** K-3 看得懂的名字 (combo! 提示用)。 */
  displayName: string;
  emoji: string;
  /** 一句因果說明 (給 toast/FX 副字,可選用)。 */
  blurb: string;
  /** 此 synergy 成立的條件 (純讀 mods,不寫)。 */
  isActive: (m: RunModifiers) => boolean;
  /**
   * 套用 synergy 的當局效果 (只寫 RunModifiers 的 synergy 旗標)。
   * 必須 idempotent:設定旗標 / 設上限值,不可累加,避免每次升級重呼就爆。
   */
  apply: (m: RunModifiers) => void;
}

/**
 * 3 組 synergy 定義。挑現有旗標最自然、因果最直接的組合:
 *
 * 1. chain-blast 連鎖爆炸 = 彈跳彈 + 爆炸彈
 *    條件:bounceCount>0 && explodeOnHitRadius>0
 *    效果:子彈每次「彈跳落點」也會爆 (本來只有第一發命中爆) → 連鎖炸場。
 *    旗標:synergyChainExplode = true。scene 在 bounce 命中時也呼 _explodeOnHit。
 *
 * 2. lucky-spread 幸運散彈 = 散彈強化 + 暴擊
 *    條件:spreadBulletBonus>0 && critChance>0
 *    效果:散彈也吃暴擊,而且暴擊機率 +15% (散彈多 → 暴擊次數爆增的爽感)。
 *    旗標:synergyCritBonus = max(現值, 0.15)。scene 算傷害時 critChance 加上它。
 *    (散彈本來就走同一條 _spawnOneBullet → _effectiveBulletDamage,暴擊已天然
 *     生效;synergy 額外給 +15% 命中率讓「散彈又暴擊」明顯有感。)
 *
 * 3. gale-rush 疾風連打 = 連射 + 加速
 *    條件:autoFireMult<1 (有連射) && speedMult>1 (有加速)
 *    效果:移動時射更快 — 自動射擊再 ×0.8 (更短間隔)。
 *    旗標:synergyFireMult = min(現值, 0.8)。scene autofire 間隔再乘它。
 */
export const SYNERGIES: SynergyDef[] = [
  {
    id: 'chain-blast',
    displayName: '連鎖爆炸',
    emoji: '💥',
    blurb: '彈跳 + 爆炸:每次彈跳都會爆!',
    isActive: (m) => m.bounceCount > 0 && m.explodeOnHitRadius > 0,
    apply: (m) => { m.synergyChainExplode = true; },
  },
  {
    id: 'lucky-spread',
    displayName: '幸運散彈',
    emoji: '🌟',
    blurb: '散彈 + 暴擊:散彈也會暴擊!',
    isActive: (m) => m.spreadBulletBonus > 0 && m.critChance > 0,
    // idempotent:設成「至少 0.15」,重複呼不會一直加。
    apply: (m) => { m.synergyCritBonus = Math.max(m.synergyCritBonus, 0.15); },
  },
  {
    id: 'gale-rush',
    displayName: '疾風連打',
    emoji: '🌪',
    blurb: '連射 + 加速:跑起來射更快!',
    isActive: (m) => m.autoFireMult < 1 && m.speedMult > 1,
    // idempotent:設成「最多到 0.8」(越小越快),重複呼不會一直變快。
    apply: (m) => { m.synergyFireMult = Math.min(m.synergyFireMult, 0.8); },
  },
];

/** 依 id 取 synergy 定義 (FX 顯示用)。找不到回 undefined。 */
export function synergyById(id: SynergyId): SynergyDef | undefined {
  return SYNERGIES.find((s) => s.id === id);
}

/**
 * 連鎖爆炸 (chain-blast) 的爆炸半徑加成倍率。
 *
 * 因果:沒湊出組合時,彈跳子彈每次命中本來就會各自爆 (爆炸彈質變的天然行為)。
 * 湊出「彈跳+爆炸」組合後,我們把爆炸範圍放大 ×1.5,讓「連鎖爆炸」明顯比「各自
 * 爆」更猛 (炸得更大、連鎖清得更乾淨),這才是組合該有的額外爽感。
 *
 * @param active synergyChainExplode 是否成立。
 * @returns 倍率:成立 = 1.5,否則 = 1 (行為同今日)。
 */
export const CHAIN_BLAST_RADIUS_MULT = 1.5;
export function chainBlastRadiusMult(active: boolean): number {
  return active ? CHAIN_BLAST_RADIUS_MULT : 1;
}

/**
 * 偵測 + 套用所有「現在成立」的 synergy,並回報「這次新成立」的清單。
 *
 * - 對每組成立的 synergy 呼 apply(m)(idempotent,寫 synergy 旗標進 mods)。
 * - 用 m.activeSynergies 記錄「已觸發過」的 id,只有「本次第一次成立」的會被回傳,
 *   讓 scene 只對新 combo 跳一次 FX 提示 (不會每次升級都重跳同一個 combo!)。
 *
 * 回傳:本次新成立的 SynergyDef 陣列 (通常 0 或 1 個)。
 *
 * 鐵則:只讀/寫 RunModifiers。不碰 progress / 修煉值。
 */
export function detectSynergies(m: RunModifiers): SynergyDef[] {
  const newly: SynergyDef[] = [];
  for (const s of SYNERGIES) {
    if (!s.isActive(m)) continue;
    s.apply(m);
    if (!m.activeSynergies.includes(s.id)) {
      m.activeSynergies.push(s.id);
      newly.push(s);
    }
  }
  return newly;
}
